// @ts-nocheck
/* eslint-disable react/display-name */
import React from "react";
import PropTypes from "prop-types";

import { GatsbyImage } from "gatsby-plugin-image";
import { BLOCKS, MARKS } from "@contentful/rich-text-types";
import { renderRichText } from "gatsby-source-contentful/rich-text";

import EmbedYouTube from "./embedYouTube";
import EmbedSpotify from "./embedSpotify";
import EmbedColumns from "./embedColumns";
import Code from "./code";
import BlockCode from "./blockCode";
import Image from "./image";

const Bold = ({ children }) => <strong>{children}</strong>;

const Text = ({ children }) => <p>{children}</p>;

export default function RichText({ styles, content, plain, maxlength }) {
    if (!content) {
        return null;
    }

    if (plain) {
        const raw = JSON.parse(content.raw);
        let text = "";
        for (const node of raw.content) {
            if (node.nodeType === "paragraph") {
                for (const subNode of node.content) {
                    if (subNode.nodeType === "text") {
                        text = `${text}${subNode.value}`;
                    }
                }
            }
        }

        if (maxlength) {
            let cropped = text.substr(0, maxlength);
            cropped = cropped.substr(0, Math.min(cropped.length, cropped.lastIndexOf(" ")));
            if (text.length > maxlength) {
                cropped = `${cropped}...`;
            }
            return <>{cropped}</>;
        }

        return <>{text}</>;
    }

    // Expects an object with raw json
    if (typeof content === "object" && "nodeType" in content) {
        content = {
            raw: JSON.stringify(content),
        };
    }

    function getHeader(children, priority) {
        const HeaderTag = `h${priority}`;
        const id =
            children &&
            children.length > 0 &&
            children[0].length > 1 &&
            children[0][1]
                .replace(/ /g, "")
                .replace(/\//g, "")
                .replace(/[^\-a-zA-Z0-9/-/#]+/g, "")
                .replace(/-{2,}/g, "")
                .replace(/(^-+|-+$)/gm, "")
                .substring(0, 45)
                .toLowerCase();
        return <HeaderTag id={id}>{children}</HeaderTag>;
    }

    const options = {
        renderMark: {
            [MARKS.BOLD]: (text) => <Bold>{text}</Bold>,
            [MARKS.CODE]: (text) => <Code>{text}</Code>,
        },
        renderNode: {
            [BLOCKS.HEADING_1]: (node, children) => getHeader(children, 1),
            [BLOCKS.HEADING_2]: (node, children) => getHeader(children, 2),
            [BLOCKS.HEADING_3]: (node, children) => getHeader(children, 3),
            [BLOCKS.HEADING_4]: (node, children) => getHeader(children, 4),
            [BLOCKS.HEADING_5]: (node, children) => getHeader(children, 5),
            [BLOCKS.HEADING_6]: (node, children) => getHeader(children, 6),

            [BLOCKS.PARAGRAPH]: (node, children) => <Text>{children}</Text>,
            [BLOCKS.EMBEDDED_ASSET]: (node) => {
                if (node.data && node.data.target && node.data.target.file) {
                    if (node.data.target.file.contentType === "video/mp4") {
                        return (
                            <video autoPlay className={styles.visual} loop muted playsInline>
                                <source src={node.data.target.file.url} type={"video/mp4"} />
                            </video>
                        );
                    } else if (node.data.target.gatsbyImageData) {
                        return (
                            <GatsbyImage alt={""} className={styles.visual} image={node.data.target.gatsbyImageData} />
                        );
                    }
                } else if (node.data?.target?.fields?.file?.contentType?.match(/^image/)) {
                    return <Image image={node.data.target.fields} />;
                }
                return null;
            },
            [BLOCKS.EMBEDDED_ENTRY]: (node) => {
                if (node.data.target) {
                    switch (node.data.target.__typename) {
                        case "ContentfulArticleEmbedYouTube":
                            return <EmbedYouTube styles={styles} url={node.data.target.url} />;
                        case "ContentfulArticleEmbedSpotify":
                            return (
                                <EmbedSpotify
                                    styles={styles}
                                    url={node.data.target.url}
                                    caption={node.data.target.caption}
                                />
                            );
                        case "ContentfulArticleEmbedColumns":
                            return (
                                <EmbedColumns
                                    styles={styles}
                                    column1={node.data.target.column1}
                                    column2={node.data.target.column2}
                                    column3={node.data.target.column3}
                                />
                            );
                        case "ContentfulArticleCodeBlock":
                            return <BlockCode>{node.data.target.code?.code}</BlockCode>;
                    }
                }

                return null;
            },
        },
        renderText: (text) => text.split("\n").flatMap((text, i) => [i > 0 && <br />, text]),
    };

    return renderRichText(content, options);
}

Bold.displayName = "Text";

Bold.propTypes = {
    children: PropTypes.array,
};

Text.displayName = "Text";

Text.propTypes = {
    children: PropTypes.array,
};

RichText.defaultProps = {
    styles: {},
};

RichText.propTypes = {
    content: PropTypes.object,
    maxlength: PropTypes.number,
    plain: PropTypes.bool,
    styles: PropTypes.object,
};
